import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Slider from "react-slick";
import { cartContext } from "../../Context/CartContext";
import { toast } from "react-hot-toast";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import RelatedProducts from "./RelatedProducts";
import { Accordion, Breadcrumb } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import ProductDescriptionSection from "./ProductDescriptionSection";
import PromotionalOffers from "./PromotionalOffers";

export default function ProductDetails() {
  let { addToCart, setnumOfCartItems } = useContext(cartContext);
  let { id } = useParams();
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [productDetails, setProductDetails] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categoryName, setCategoryName] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [userNote, setUserNote] = useState(""); // For Laptop Skin
  const [keyboardNote, setKeyboardNote] = useState(""); // For Keyboard Stickers
  const [Languages, setLanguages] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [isSoldOut, setIsSoldOut] = useState(false);

  async function getProductDetails() {
    setisLoading(true);
    try {
      let { data } = await axios.get(
        `https://api.elza2.store/api/v1/products/${id}`
      );
      setProductDetails(data.product);
      setIsSoldOut(data.product.soldOut);
      if (data.product.category) {
        const categoryResponse = await axios.get(
          `https://api.elza2.store/api/v1/categories/${data.product.category._id}`
        );
        setCategoryName(categoryResponse.data.category.name);
        setSizes(data.product.category.sizes);
      }
    } catch (error) {
      toast.error("Error loading product details");
    }
    setisLoading(false);
  }

  useEffect(() => {
    getProductDetails();
  }, [id]);

  const hasSizes = sizes.length > 0;
  const minPrice = hasSizes
    ? Math.min(...sizes.map((size) => size.price))
    : productDetails?.price;
  const maxPrice = hasSizes
    ? Math.max(...sizes.map((size) => size.price))
    : productDetails?.price;

  const handleDecreaseQty = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncreaseQty = () => {
    if (quantity < 10) {
      setQuantity(quantity + 1);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUserImage(file);
    }
  };

  const handleFeatureSelection = (feature, isSelected) => {
    setSelectedFeatures((prevFeatures) => {
      if (isSelected) {
        return [...prevFeatures, { ...feature, note: "" }];
      }
      return prevFeatures.filter((f) => f.id !== feature.id);
    });
  };

  async function addProduct(
    productId,
    quantity,
    size,
    image,
    note,
    Languages,
    features
  ) {
    const isLoggedIn = !!localStorage.getItem("Auth");
    if (isSoldOut) {
      return;
    }
    if (!isLoggedIn) {
      toast.error("Please login to add items to cart", { duration: 2000 });
      navigate("/login");
      return;
    }

    if (productDetails.sizes && productDetails.sizes.length > 0 && !size) {
      toast.error("Please select a size", { duration: 2000 });
      return;
    }

    if (productDetails.sizes && productDetails.sizes.length > 0 && !image) {
      toast.error("Please upload an image", { duration: 2000 });
      return;
    }

    setIsAddingToCart(true);
    try {
      let response = await addToCart(
        productId,
        quantity,
        size,
        image,
        note,
        Languages,
        features
      );
      if (response.status === 200) {
        toast.success(response.data.message, { duration: 2000 });
        setnumOfCartItems(response.data.numOfCartItems);
      }
    } catch (error) {
      toast.error("Failed to add item to cart", { duration: 2000 });
    }
    setIsAddingToCart(false);
  }

  const handleAddToCart = () => {
    // Check for the product's required fields (size, image, note)
    if (
      productDetails?.category?.name === "Laptop Skin" &&
      (!userNote || userNote.trim() === "")
    ) {
      toast.error("Please add your laptop model", { duration: 2000 });
      return;
    }

    if (
      productDetails?.category?.name === "Keyboard Stickers" &&
      (!keyboardNote || keyboardNote.trim() === "")
    ) {
      toast.error("Please add your keyboard model", { duration: 2000 });
      return;
    }
    if (productDetails?.category?.name === "Keyboard Stickers" && !userImage) {
      toast.error("Please upload Laptop Image", { duration: 2000 });
      return;
    }

    if (
      productDetails?.category?.name === "Posters" &&
      (!selectedSize || selectedSize.trim() === "")
    ) {
      toast.error("Please Select a size", { duration: 2000 });
      return;
    }

    // Convert userNote to an array (if it's not already)
    const notes = userNote
      ? userNote.split(",").map((note) => note.trim())
      : [];

    // Convert selectedFeatures to an array of feature IDs
    const featureIds = selectedFeatures.map((feature) => feature._id);

    // Prepare the final note to send based on category
    const noteToSend =
      productDetails?.category?.name === "Laptop Skin" ? notes : keyboardNote;

    addProduct(
      id,
      quantity,
      selectedSize,
      userImage,
      noteToSend,
      Languages,
      featureIds
    );
  };

  const handleThumbnailClick = (index) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
      setCurrentSlide(index);
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current, next) => setCurrentSlide(next),
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    customPaging: (i) => (
      <div
        className={`w-3 h-3 mx-1 rounded-full transition-all duration-300 ${
          i === currentSlide ? "explore-btn" : "bg-gray-300"
        }`}
      />
    ),
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
        <div className="py-4">
          <Breadcrumb className="px-5 rounded-lg">
            <Breadcrumb.Item>
              <Link
                to="/"
                className="flex items-center text-gray-700 hover:text-blue-600"
              >
                <HiHome className="w-4 h-4 mr-2" />
                Home
              </Link>
            </Breadcrumb.Item>
            {categoryName && (
              <Breadcrumb.Item>
                <Link
                  to={`/products?category=${productDetails?.category?._id}`}
                  className="text-gray-700 hover:text-blue-600"
                >
                  {categoryName}
                </Link>
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item>
              <span className="text-gray-400">{productDetails?.title}</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>


        <section className="overflow-hidden bg-white py-11 font-poppins dark:bg-gray-800 rounded-xl shadow-lg">
          <PromotionalOffers
            productDetails={productDetails?.category?.promotion}
          />
          <div className="max-w-6xl px-4 py-4 mx-auto lg:py-8 md:px-6">
            <div className="flex flex-wrap -mx-4">
              {/* Product Images - Left Column */}
              <div className="w-full mb-8 md:w-1/2 md:mb-0">
                <div className="sticky top-0 overflow-hidden">
                  <div className="relative mb-6 lg:mb-10 lg:h-[500px] rounded-xl overflow-hidden">
                    <Slider
                      ref={sliderRef}
                      {...sliderSettings}
                      className="product-slider"
                    >
                      {productDetails?.images?.map((img, index) => (
                        <div key={index} className="outline-none">
                          <img
                            src={img}
                            alt={`${productDetails?.title} - View ${index + 1}`}
                            className="object-contain w-full h-[500px]"
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    {productDetails?.images?.map((img, index) => (
                      <button
                        key={index}
                        className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                          currentSlide === index
                            ? "ring-2 ring-blue-500"
                            : "hover:opacity-75"
                        }`}
                        onClick={() => handleThumbnailClick(index)}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          className="object-cover w-full h-20"
                        />
                      </button>
                    ))}
                  </div>

                </div>
              </div>

              {/* Product Details - Right Column */}
              <div className="w-full px-4 md:w-1/2">
                <div className="lg:pl-20">
                  <div className="mb-8">
                    {isSoldOut ? (
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-danger rounded-full">
                        Sold Out
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 text-xs font-semibold explore-btn rounded-full">
                        In Stock
                      </span>
                    )}
                    <h2 className="max-w-xl mt-4 mb-6 text-2xl font-bold dark:text-gray-400 md:text-4xl">
                      {productDetails?.title}
                    </h2>
                    <div className="flex items-center mb-6">
                      <p className="text-4xl text-main font-bold dark:text-blue-400">
                        {hasSizes
                          ? `${minPrice} ~ ${maxPrice} EGP`
                          : `${productDetails?.price} EGP`}
                      </p>
                    </div>
                  </div>

                  {productDetails?.category?.sizes &&
                    productDetails?.category?.sizes.length > 0 && (
                      <div className="mb-8">
                        <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                          Size
                        </label>
                        <select
                          onChange={(e) => setSelectedSize(e.target.value)}
                          value={selectedSize}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md"
                          required={
                            !!(
                              productDetails?.category?.sizes &&
                              productDetails?.category?.sizes.length > 0
                            )
                          }
                        >
                          <option value="">Select Size</option>
                          {productDetails?.category.sizes.map((size) => (
                            <option key={size._id} value={size.size}>
                              {size.size} - {size.price} EGP
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  <div className="mb-8">
                    <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                      Quantity
                    </label>
                    <div className="flex items-center mt-2">
                      <button
                        onClick={handleDecreaseQty}
                        className="flex items-center justify-center w-10 h-10 text-gray-600 border border-gray-300 rounded-l hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
                      >
                        <span className="text-2xl font-thin">-</span>
                      </button>
                      <span className="flex items-center justify-center w-12 h-10 text-xl font-semibold text-gray-700">
                        {quantity}
                      </span>
                      <button
                        onClick={handleIncreaseQty}
                        className="flex items-center justify-center w-10 h-10 text-gray-600 border border-gray-300 rounded-r hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
                      >
                        <span className="text-2xl font-thin">+</span>
                      </button>
                    </div>
                  </div>

                  {productDetails?.category?.name === "Keyboard Stickers" && (
                    <div className="mb-8">
                      <div className="mb-8">
                        <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                          Take a photo for your keyboard for a suitable size
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md"
                          required={
                            !!(
                              productDetails?.category?.sizes &&
                              productDetails?.category?.sizes.length > 0
                            )
                          }
                        />
                        {userImage && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Uploaded Image: {userImage.name}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="mb-8">
                        <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                          Add your Laptop or Keyboard Model*
                        </label>
                        <textarea
                          value={keyboardNote}
                          onChange={(e) => setKeyboardNote(e.target.value)}
                          placeholder="Write your model here..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-md"
                          required={true}
                        />
                      </div>
                    </div>
                  )}

                  {productDetails?.category?.name === "Laptop Skin" && (
                    <div className="mb-8">
                      <div className="mb-8">
                        <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                          Add your Laptop Model*
                        </label>
                        <textarea
                          value={userNote}
                          onChange={(e) => setUserNote(e.target.value)}
                          placeholder="Write your model here..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-md"
                          required={true}
                        />
                      </div>
                    </div>
                  )}

                  {productDetails?.category?.name === "Keyboard Stickers" && (
                    <div className="mb-8">
                      {productDetails?.category?.additionalFeatures.map(
                        (feature, index) => (
                          <div key={index} className="flex items-center mb-4">
                            <input
                              type="checkbox"
                              id={`feature-${index}`}
                              value={feature.price}
                              onChange={(e) =>
                                handleFeatureSelection(
                                  feature,
                                  e.target.checked
                                )
                              }
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label
                              htmlFor={`feature-${index}`}
                              className="ml-2 text-sm text-gray-700 dark:text-gray-400"
                            >
                              {feature.feature} (+{feature.price})
                            </label>
                            <div className="mb-8">
                              <label className="block mb-3 text-sm font-semibold text-gray-700 dark:text-gray-400">
                                What is the Language? (Optional) (+20 EGP)
                              </label>
                              <input
                                type="text"
                                value={Languages[index]}
                                onChange={(e) => {
                                  const newLanguages = [...Languages];
                                  newLanguages[index] = e.target.value;
                                  setLanguages(newLanguages);
                                }}
                                placeholder="French, Deutsch, etc..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  <ProductDescriptionSection productDetails={productDetails} />

                  <div className="flex flex-col gap-4">
                    <button
                      onClick={handleAddToCart}
                      className="w-full py-4 explore-btn text-sm font-bold rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isAddingToCart || isSoldOut}
                    >
                      {isAddingToCart ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Adding to Cart...
                        </>
                      ) : isSoldOut ? (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M9.293 14.707a1 1 0 001.414 0L17 8.414V14a1 1 0 102 0V6a1 1 0 00-1-1h-8a1 1 0 100 2h5.586l-6.293 6.293a1 1 0 000 1.414z" />
                          </svg>
                          Sold Out
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                          </svg>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-8 border-t border-gray-200 pt-8">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center text-gray-500">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Best Quality
                      </div>
                      <div className="flex items-center text-gray-500">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        Secure Payment
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ad before Related Products */}

        <RelatedProducts
          categoryId={productDetails.category}
          currentProductId={id}
          productName={productDetails.title}
        />

      </div>
    </>
  );
}